<template>
  <div
    v-if="props.show"
    class="fixed z-10 inset-0 bg-gray-900 bg-opacity-60 overflow-y-scroll flex items-center justify-center text-base"
  >
    <div class="bg-white rounded-lg shadow-xl w-1/2 h-[30vh] overflow-y-auto">
      <div class="flex items-center justify-between p-4 border-b">
        <h4 class="text-2xl text-gray-800 font-bold">Create Company</h4>
        <button
          type="button"
          class="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex text-gray-500 items-center justify-end h-8 w-8 hover:text-yellow-500"
          @click="handleCloseModal"
          aria-label="Close"
        >
          <span class="sr-only">Dismiss</span>
          <X :size="24" />
        </button>
      </div>
      <form
        @submit.prevent="handleSubmitForm"
        class="space-y-4 p-6"
      >
        <div class="grid grid-cols-1 gap-y-6 px-4">
          <!-- Company Name -->
          <div>
            <label for="name" class="block text-sm font-medium text-gray-700">
              Company Name <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              v-model="formData.name"
              placeholder="Enter company name..."
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring focus:ring-yellow-500 focus:ring-opacity-50 bg-gray-50 p-2"
            />
            <span class="text-sm text-red-500" v-if="errors.name">{{ errors.name }}</span>
          </div>
        </div>

        <div class="flex justify-end mt-6 gap-y-6 px-4 pb-6">
          <button
            type="submit"
            :disabled="showLoader"
            class="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50"
          >
            <span v-if="!showLoader">Create</span>
            <span v-else class="flex items-center">
              <svg
                class="animate-spin h-5 w-5 text-white mr-2"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                  fill="none"
                />
              </svg>
              Creating...
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from "vue";
import { X } from "lucide-vue-next";
import apiClient from "@/services/api";
import { useToast } from "vue-toast-notification";

const toast = useToast({
  position: 'top-right',
  duration: 3000,
  dismissible: true,
  pauseOnHover: true,
  type: 'default',
});

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  }
});

const emits = defineEmits(['close', 'created']);

// Form state
const formData = ref({
  name: ''
});

// UI State
const showLoader = ref(false);
const errors = ref({});

const validateForm = () => {
  errors.value = {};
  let isValid = true;

  // Validate name
  if (!formData.value.name || formData.value.name.trim() === '') {
    errors.value.name = 'Company name is required.';
    isValid = false;
  }

  return isValid;
};

const handleSubmitForm = async () => {
  if (!validateForm()) {
    toast.error("Please address the errors on the form.");
    return;
  }

  showLoader.value = true;

  try {
    const payload = {
      company: {
        name: formData.value.name
      }
    };

    const response = await apiClient.createCompany(payload);

    if (response.success) {
      toast.success("Company created successfully.");
      emits('created');
      handleCloseModal();
    } else {
      toast.error(response.errors || "Failed to create company");
    }
  } catch (error) {
      toast.error('Company name already taken' || error);
  } finally {
    showLoader.value = false;
  }
};

const handleCloseModal = () => {
  formData.value.name = ''; // Reset form
  errors.value = {};
  emits('close');
};
</script>